import React, { memo, useMemo } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import StatusIconCDS from 'app/shared/componentsRestyled/coreDesignSystem/StatusIconCDS';
import PropTypes from 'prop-types';

import { StatusEnum } from 'constants/status';

import { summarizeFormReview } from '../../formReview.utils';
import { FORM_REVIEW_LABELS, FormReviewTestIds } from '../../FormReviewCard.constants';
import {
  signOffActionsStyles,
  signOffCountsStyles,
  signOffNoticeStyles,
  signOffSummaryStyles,
} from '../FormReviewSurface.styles';

const FormReviewSignOffSummary = ({ form, isInFlight, onConfirm, onCancel }) => {
  const summary = useMemo(() => summarizeFormReview(form), [form]);
  const isSchemaDrifted = form?.schemaVersionMatches === false;
  const isConfirmDisabled = isInFlight || isSchemaDrifted;

  const counts = useMemo(
    () => [
      { label: FORM_REVIEW_LABELS.SIGN_OFF_AGREED, value: summary.agreed },
      { label: FORM_REVIEW_LABELS.SIGN_OFF_OVERRIDDEN, value: summary.overridden },
      { label: FORM_REVIEW_LABELS.SIGN_OFF_FLAGGED, value: summary.flagged },
      { label: FORM_REVIEW_LABELS.SIGN_OFF_PENDING, value: summary.pending },
      { label: FORM_REVIEW_LABELS.SIGN_OFF_CHANGED, value: summary.changedAfterReview },
    ],
    [summary],
  );

  return (
    <Box data-testid={FormReviewTestIds.SIGN_OFF_SUMMARY} sx={signOffSummaryStyles}>
      <Typography variant="bold12">{FORM_REVIEW_LABELS.SIGN_OFF_TITLE}</Typography>
      <Box sx={signOffCountsStyles}>
        {counts.map(count => (
          <Typography color="text.secondary" key={count.label} variant="regular12">
            {`${count.label}: ${count.value}`}
          </Typography>
        ))}
      </Box>
      {summary.hasBlockingFields ? (
        <Box data-testid={FormReviewTestIds.SIGN_OFF_BLOCKING} sx={signOffNoticeStyles}>
          <StatusIconCDS size={16} type={StatusEnum.ERROR} />
          <Typography color="error.dark" variant="regular12">
            {FORM_REVIEW_LABELS.SIGN_OFF_BLOCKING}
          </Typography>
        </Box>
      ) : null}
      {isSchemaDrifted ? (
        <Box data-testid={FormReviewTestIds.SIGN_OFF_SCHEMA_DRIFT} sx={signOffNoticeStyles}>
          <StatusIconCDS size={16} type={StatusEnum.WARNING} />
          <Typography color="warning.dark" variant="regular12">
            {FORM_REVIEW_LABELS.SIGN_OFF_SCHEMA_DRIFT}
          </Typography>
        </Box>
      ) : null}
      <Box sx={signOffActionsStyles}>
        <Button
          data-testid={FormReviewTestIds.SIGN_OFF_CANCEL}
          disabled={isInFlight}
          size="small"
          variant="line"
          onClick={onCancel}
        >
          {FORM_REVIEW_LABELS.SIGN_OFF_CANCEL}
        </Button>
        <Button
          data-testid={FormReviewTestIds.SIGN_OFF_CONFIRM}
          disabled={isConfirmDisabled}
          size="small"
          onClick={onConfirm}
        >
          {FORM_REVIEW_LABELS.SIGN_OFF_CONFIRM}
        </Button>
        {isInFlight ? <CircularProgress size={14} thickness={4} /> : null}
      </Box>
    </Box>
  );
};

FormReviewSignOffSummary.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isInFlight: PropTypes.bool,
  form: PropTypes.shape({
    schemaVersionMatches: PropTypes.bool,
    fields: PropTypes.array,
  }),
};

export default memo(FormReviewSignOffSummary);
